// src/services/storageService.js - Comprehensive Firebase Storage Service
// Handles all user data storage with support for local, cloud, or both

import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  collection,
  getDocs,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './firebase';
import { deleteCloudBackup } from './cloudBackup';

// ============================================================================
// ASSESSMENT STORAGE
// ============================================================================

export const saveAssessmentResults = async (userId, assessmentData, storagePreference = 'both') => {
  try {
    // Save to localStorage if preference allows
    if (storagePreference === 'local' || storagePreference === 'both') {
      localStorage.setItem('artOfStory_assessmentResults', JSON.stringify(assessmentData));
    }

    // Save to Firestore if preference allows
    if (storagePreference === 'cloud' || storagePreference === 'both') {
      const assessmentRef = doc(db, 'users', userId, 'data', 'assessment');
      await setDoc(assessmentRef, {
        ...assessmentData,
        updatedAt: serverTimestamp(),
        savedAt: new Date().toISOString()
      }, { merge: true });
    }

    console.log('✅ Assessment results saved successfully');
    return true;
  } catch (error) {
    console.error('❌ Error saving assessment results:', error);
    return false;
  }
};

export const loadAssessmentResults = async (userId, storagePreference = 'both') => {
  try {
    // Try cloud first if preference allows
    if (storagePreference === 'cloud' || storagePreference === 'both') {
      const assessmentRef = doc(db, 'users', userId, 'data', 'assessment');
      const assessmentDoc = await getDoc(assessmentRef);

      if (assessmentDoc.exists()) {
        const data = assessmentDoc.data();
        console.log('✅ Loaded assessment from Firebase');

        // Sync to local if 'both'
        if (storagePreference === 'both') {
          localStorage.setItem('artOfStory_assessmentResults', JSON.stringify(data));
        }

        return data;
      }
    }

    // Fall back to localStorage
    if (storagePreference === 'local' || storagePreference === 'both') {
      const localData = localStorage.getItem('artOfStory_assessmentResults');
      if (localData) {
        console.log('✅ Loaded assessment from localStorage');
        return JSON.parse(localData);
      }
    }

    return null;
  } catch (error) {
    console.error('❌ Error loading assessment results:', error);

    // Try localStorage as fallback
    const localData = localStorage.getItem('artOfStory_assessmentResults');
    return localData ? JSON.parse(localData) : null;
  }
};

// ============================================================================
// MODULE PROGRESS STORAGE
// ============================================================================

export const saveModuleProgress = async (userId, moduleId, progressData, storagePreference = 'both') => {
  try {
    // Save to localStorage if preference allows
    if (storagePreference === 'local' || storagePreference === 'both') {
      const allProgress = JSON.parse(localStorage.getItem('artOfStory_moduleProgress') || '{}');
      allProgress[moduleId] = {
        ...progressData,
        updatedAt: new Date().toISOString()
      };
      localStorage.setItem('artOfStory_moduleProgress', JSON.stringify(allProgress));
    }

    // Save to Firestore if preference allows
    if (storagePreference === 'cloud' || storagePreference === 'both') {
      const progressRef = doc(db, 'users', userId, 'moduleProgress', moduleId);
      await setDoc(progressRef, {
        ...progressData,
        updatedAt: serverTimestamp(),
        savedAt: new Date().toISOString()
      }, { merge: true });

      // Update user stats
      await updateUserStats(userId, moduleId, progressData);
    }

    console.log(`✅ Module ${moduleId} progress saved successfully`);
    return true;
  } catch (error) {
    console.error(`❌ Error saving module ${moduleId} progress:`, error);
    return false;
  }
};

export const loadModuleProgress = async (userId, storagePreference = 'both') => {
  try {
    // Try cloud first if preference allows
    if (storagePreference === 'cloud' || storagePreference === 'both') {
      const progressCollection = collection(db, 'users', userId, 'moduleProgress');
      const snapshot = await getDocs(progressCollection);

      if (!snapshot.empty) {
        const progress = {};
        snapshot.forEach(doc => {
          progress[doc.id] = doc.data();
        });

        console.log('✅ Loaded module progress from Firebase');

        // Sync to local if 'both'
        if (storagePreference === 'both') {
          localStorage.setItem('artOfStory_moduleProgress', JSON.stringify(progress));
        }

        return progress;
      }
    }

    // Fall back to localStorage
    if (storagePreference === 'local' || storagePreference === 'both') {
      const localData = localStorage.getItem('artOfStory_moduleProgress');
      if (localData) {
        console.log('✅ Loaded module progress from localStorage');
        return JSON.parse(localData);
      }
    }

    return {};
  } catch (error) {
    console.error('❌ Error loading module progress:', error);

    // Try localStorage as fallback
    const localData = localStorage.getItem('artOfStory_moduleProgress');
    return localData ? JSON.parse(localData) : {};
  }
};

const updateUserStats = async (userId, moduleId, progress) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      const stats = userDoc.data().stats || {};

      // Update completion stats
      if (progress.completed && !stats.completedModules?.includes(moduleId)) {
        await updateDoc(userRef, {
          'stats.completedModules': [...(stats.completedModules || []), moduleId],
          'stats.totalModulesCompleted': (stats.totalModulesCompleted || 0) + 1,
          'stats.lastActivityAt': serverTimestamp()
        });
      }
    } else {
      // Create user stats document if it doesn't exist
      await setDoc(userRef, {
        stats: {
          completedModules: progress.completed ? [moduleId] : [],
          totalModulesCompleted: progress.completed ? 1 : 0,
          createdAt: serverTimestamp(),
          lastActivityAt: serverTimestamp()
        }
      });
    }
  } catch (error) {
    console.error('Error updating user stats:', error);
  }
};

// ============================================================================
// TOOL DATA STORAGE
// ============================================================================

export const saveToolData = async (userId, toolId, toolData, storagePreference = 'both') => {
  try {
    // Save to localStorage if preference allows
    if (storagePreference === 'local' || storagePreference === 'both') {
      const allToolData = JSON.parse(localStorage.getItem('artOfStory_toolData') || '{}');
      allToolData[toolId] = {
        ...toolData,
        updatedAt: new Date().toISOString()
      };
      localStorage.setItem('artOfStory_toolData', JSON.stringify(allToolData));
    }

    // Save to Firestore if preference allows
    if (storagePreference === 'cloud' || storagePreference === 'both') {
      const toolRef = doc(db, 'users', userId, 'toolData', toolId);
      await setDoc(toolRef, {
        ...toolData,
        updatedAt: serverTimestamp(),
        savedAt: new Date().toISOString()
      }, { merge: true });
    }

    console.log(`✅ Tool ${toolId} data saved successfully`);
    return true;
  } catch (error) {
    console.error(`❌ Error saving tool ${toolId} data:`, error);
    return false;
  }
};

export const loadToolData = async (userId, storagePreference = 'both') => {
  try {
    // Try cloud first if preference allows
    if (storagePreference === 'cloud' || storagePreference === 'both') {
      const toolCollection = collection(db, 'users', userId, 'toolData');
      const snapshot = await getDocs(toolCollection);

      if (!snapshot.empty) {
        const toolData = {};
        snapshot.forEach(doc => {
          toolData[doc.id] = doc.data();
        });

        console.log('✅ Loaded tool data from Firebase');

        // Sync to local if 'both'
        if (storagePreference === 'both') {
          localStorage.setItem('artOfStory_toolData', JSON.stringify(toolData));
        }

        return toolData;
      }
    }

    // Fall back to localStorage
    if (storagePreference === 'local' || storagePreference === 'both') {
      const localData = localStorage.getItem('artOfStory_toolData');
      if (localData) {
        console.log('✅ Loaded tool data from localStorage');
        return JSON.parse(localData);
      }
    }

    return {};
  } catch (error) {
    console.error('❌ Error loading tool data:', error);

    // Try localStorage as fallback
    const localData = localStorage.getItem('artOfStory_toolData');
    return localData ? JSON.parse(localData) : {};
  }
};

export const saveToolUsage = async (userId, toolId, usageData, storagePreference = 'both') => {
  try {
    // Save to localStorage if preference allows
    if (storagePreference === 'local' || storagePreference === 'both') {
      const allUsage = JSON.parse(localStorage.getItem('artOfStory_toolUsage') || '{}');
      allUsage[toolId] = {
        ...usageData,
        updatedAt: new Date().toISOString()
      };
      localStorage.setItem('artOfStory_toolUsage', JSON.stringify(allUsage));
    }

    // Save to Firestore if preference allows
    if (storagePreference === 'cloud' || storagePreference === 'both') {
      const usageRef = doc(db, 'users', userId, 'toolUsage', toolId);
      await setDoc(usageRef, {
        ...usageData,
        updatedAt: serverTimestamp(),
        savedAt: new Date().toISOString()
      }, { merge: true });
    }

    return true;
  } catch (error) {
    console.error(`❌ Error saving tool ${toolId} usage:`, error);
    return false;
  }
};

export const loadToolUsage = async (userId, storagePreference = 'both') => {
  try {
    // Try cloud first if preference allows
    if (storagePreference === 'cloud' || storagePreference === 'both') {
      const usageCollection = collection(db, 'users', userId, 'toolUsage');
      const snapshot = await getDocs(usageCollection);

      if (!snapshot.empty) {
        const usage = {};
        snapshot.forEach(doc => {
          usage[doc.id] = doc.data();
        });

        console.log('✅ Loaded tool usage from Firebase');

        // Sync to local if 'both'
        if (storagePreference === 'both') {
          localStorage.setItem('artOfStory_toolUsage', JSON.stringify(usage));
        }

        return usage;
      }
    }

    // Fall back to localStorage
    if (storagePreference === 'local' || storagePreference === 'both') {
      const localData = localStorage.getItem('artOfStory_toolUsage');
      if (localData) {
        console.log('✅ Loaded tool usage from localStorage');
        return JSON.parse(localData);
      }
    }

    return {};
  } catch (error) {
    console.error('❌ Error loading tool usage:', error);

    // Try localStorage as fallback
    const localData = localStorage.getItem('artOfStory_toolUsage');
    return localData ? JSON.parse(localData) : {};
  }
};

// ============================================================================
// DATA LOADING & MIGRATION
// ============================================================================

export const loadAllUserData = async (userId, storagePreference = 'both') => {
  try {
    console.log('📥 Loading all user data...');

    const [assessment, moduleProgress, toolData, toolUsage] = await Promise.all([
      loadAssessmentResults(userId, storagePreference),
      loadModuleProgress(userId, storagePreference),
      loadToolData(userId, storagePreference),
      loadToolUsage(userId, storagePreference)
    ]);

    console.log('✅ All user data loaded successfully');

    return {
      assessmentResults: assessment,
      moduleProgress: moduleProgress,
      toolData: toolData,
      toolUsage: toolUsage
    };
  } catch (error) {
    console.error('❌ Error loading all user data:', error);
    return {
      assessmentResults: null,
      moduleProgress: {},
      toolData: {},
      toolUsage: {}
    };
  }
};

// ============================================================================
// DATA CLEANUP
// ============================================================================

export const clearLocalData = () => {
  try {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith('artOfStory_')) {
        localStorage.removeItem(key);
      }
    });
    console.log('✅ Local data cleared');
    return true;
  } catch (error) {
    console.error('❌ Error clearing local data:', error);
    return false;
  }
};

export const clearCloudData = async (userId) => {
  if (!userId) {
    console.error('No user ID provided for cloud data clearing');
    return false;
  }

  try {
    // One implementation for the whole app (services/cloudBackup.js). The old
    // inline version ended with updateDoc on users/{uid}, which throws when
    // that document was never created, so it reported failure for anyone who
    // hadn't started a module.
    await deleteCloudBackup(db, userId);

    console.log('✅ Cloud data cleared');
    return true;
  } catch (error) {
    console.error('❌ Error clearing cloud data:', error);
    return false;
  }
};

// ============================================================================
// MIGRATION HELPERS
// ============================================================================

export const migrateOldLocalStorage = () => {
  try {
    // Migrate old artOfConversation_ keys to artOfStory_ keys
    const keys = Object.keys(localStorage);
    let migrated = 0;

    keys.forEach(key => {
      if (key.startsWith('artOfConversation_')) {
        const newKey = key.replace('artOfConversation_', 'artOfStory_');
        const value = localStorage.getItem(key);
        localStorage.setItem(newKey, value);
        localStorage.removeItem(key);
        migrated++;
      }
    });

    if (migrated > 0) {
      console.log(`✅ Migrated ${migrated} localStorage keys from artOfConversation_ to artOfStory_`);
    }

    return migrated;
  } catch (error) {
    console.error('❌ Error migrating localStorage:', error);
    return 0;
  }
};
